import DOMPurify from "dompurify";
import { marked } from "marked";
import type { NewsPost, SiteContent, SiteSectionKey } from "../types";
import { defaultContent } from "../data/defaultContent";
import { supabase } from "./supabase";

const archiveFiles = import.meta.glob("../../content/posts/*.md", {
  query: "?raw",
  import: "default",
  eager: true,
}) as Record<string, string>;

function stripQuotes(value: string): string {
  return value.replace(/^['"]|['"]$/g, "").trim();
}

function slugFromPath(path: string): string {
  return path
    .split("/")
    .pop()!
    .replace(/\.md$/i, "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[„“”]/g, "")
    .replace(/[^a-zA-Z0-9-]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-|-$/g, "")
    .toLowerCase();
}

export const NEWS_EXCERPT_LENGTH = 190;

export function createNewsExcerpt(body: string, maxLength = NEWS_EXCERPT_LENGTH): string {
  const plain = body
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/<[^>]+>/g, " ")
    .replace(/^\s*#{1,6}\s+/gm, "")
    .replace(/^\s*>\s?/gm, "")
    .replace(/^\s*(?:[-*+]|\d+\.)\s+/gm, "")
    .replace(/[*_~]/g, "")
    .replace(/\s+/g, " ")
    .trim();

  if (plain.length <= maxLength) return plain;
  const shortened = plain.slice(0, Math.max(1, maxLength - 1));
  const lastSpace = shortened.lastIndexOf(" ");
  const wordSafeEnd = lastSpace >= Math.floor(maxLength * .65) ? shortened.slice(0, lastSpace) : shortened;
  return `${wordSafeEnd.trimEnd()}…`;
}

function parseArchivePost(path: string, raw: string): NewsPost | null {
  const match = raw.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/);
  if (!match) return null;

  const metadata: Record<string, string> = {};
  const images: string[] = [];
  let listKey = "";

  match[1].split(/\r?\n/).forEach((line) => {
    const listMatch = line.match(/^\s+-\s+(.+)$/);
    if (listMatch && listKey === "images") {
      images.push(stripQuotes(listMatch[1]));
      return;
    }
    const fieldMatch = line.match(/^([a-zA-Z_]+):\s*(.*)$/);
    if (!fieldMatch) return;
    const [, key, value] = fieldMatch;
    metadata[key] = stripQuotes(value);
    listKey = value ? "" : key;
  });

  if (metadata.draft === "true") return null;
  const body = match[2].trim();
  const slug = slugFromPath(path);

  return {
    id: `archive-${slug}`,
    slug,
    title: metadata.title || "Vereinsnachricht",
    excerpt: createNewsExcerpt(body),
    body,
    cover_image: metadata.prev_image || null,
    images,
    status: "published",
    published_at: metadata.date ? new Date(metadata.date.replace(" ", "T")).toISOString() : new Date(0).toISOString(),
    source: "archive",
  };
}

export const archivePosts: NewsPost[] = Object.entries(archiveFiles)
  .map(([path, raw]) => parseArchivePost(path, raw))
  .filter((post): post is NewsPost => Boolean(post))
  .sort((a, b) => new Date(b.published_at).getTime() - new Date(a.published_at).getTime());

export function toSafeHtml(markdown: string): string {
  const html = marked.parse(markdown, { gfm: true, breaks: true }) as string;
  return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
}

export async function fetchSiteContent(): Promise<SiteContent> {
  const { data, error } = await supabase.from("site_sections").select("key,data");
  if (error || !data?.length) return defaultContent;

  const merged = structuredClone(defaultContent);
  data.forEach((row) => {
    const key = row.key as SiteSectionKey;
    if (key in merged && row.data && typeof row.data === "object") {
      (merged[key] as unknown) = { ...merged[key], ...row.data };
    }
  });
  return merged;
}

export async function fetchPublishedNews(): Promise<NewsPost[]> {
  const { data, error } = await supabase
    .from("news_posts")
    .select("*")
    .eq("status", "published")
    .order("published_at", { ascending: false });

  if (error) return archivePosts;

  const remote = ((data || []) as NewsPost[]).map((post) => ({
    ...post,
    excerpt: createNewsExcerpt(post.body),
    images: Array.isArray(post.images) ? post.images : [],
    source: "database" as const,
  }));
  return remote;
}

export async function fetchAdminNews(): Promise<NewsPost[]> {
  const { data, error } = await supabase
    .from("news_posts")
    .select("*")
    .order("published_at", { ascending: false });
  if (error) throw error;
  return ((data || []) as NewsPost[]).map((post) => ({
    ...post,
    excerpt: createNewsExcerpt(post.body),
    images: Array.isArray(post.images) ? post.images : [],
  }));
}

export async function importArchivePosts(): Promise<number> {
  const rows = archivePosts.map(({ id: _id, source: _source, ...post }) => post);
  const { error } = await supabase.from("news_posts").upsert(rows, { onConflict: "slug" });
  if (error) throw error;
  return rows.length;
}
